import { useState, useEffect, useRef } from 'react';
import { PopUpModal } from '../';
import styles from './StartDesignModal.module.css';
import burst from './assets/burst.svg';

export default function StartDesignModal({ 
    isOpen, 
    onClose, 
    floorplanName,
    communities = [],
    elevations = [],
    scenes = [],
    onStartDesigning
}) {
    const [selectedCommunity, setSelectedCommunity] = useState('');
    const [selectedElevation, setSelectedElevation] = useState(null);
    const [selectedScene, setSelectedScene] = useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Auto-select community if only one option
    useEffect(() => {
        if (communities.length === 1 && !selectedCommunity) {
            setSelectedCommunity(communities[0]);
        }
    }, [communities, selectedCommunity]);

    // Auto-select first elevation when elevations are available
    useEffect(() => {
        if (elevations.length > 0 && !selectedElevation) {
            setSelectedElevation(elevations[0]);
        }
    }, [elevations, selectedElevation]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleStartDesigning = () => {
        if (selectedCommunity && selectedElevation && selectedScene) {
            onStartDesigning({
                community: selectedCommunity,
                elevation: selectedElevation,
                scene: selectedScene
            });
        }
    };

    const handleCommunitySelect = (community) => {
        setSelectedCommunity(community);
        setIsDropdownOpen(false);
    };

    // Filter scenes based on selected elevation
    // Only show scenes (rooms) that belong to the selected elevation
    const filteredScenes = selectedElevation 
        ? scenes.filter(scene => scene.elevationId === selectedElevation.id)
        : [];

    const isComplete = selectedCommunity && selectedElevation && selectedScene;

    // Don't render if not open
    if (!isOpen) return null;

    return (
        <PopUpModal handleClose={onClose}>
            <div className={styles.container}>
                {/* Step 1: Select Community */}
                <div className={styles.step}>
                    <h2 className={styles.stepTitle}><span>Step 1:</span> Select your Community</h2>
                    <div className={styles.customDropdown} ref={dropdownRef}>
                        <div 
                            className={styles.dropdownHeader}
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        >
                            <span>{selectedCommunity || 'Select Community'}</span>
                            <img 
                                src={burst} 
                                alt="dropdown icon" 
                                className={`${styles.dropdownIcon} ${isDropdownOpen ? styles.spinning : ''}`}
                            />
                        </div>
                        {isDropdownOpen && (
                            <div className={styles.dropdownList}>
                                {communities.map((community, index) => (
                                    <div
                                        key={index}
                                        className={styles.dropdownItem}
                                        onClick={() => handleCommunitySelect(community)}
                                    >
                                        {community}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Step 2: Choose Elevation */}
                <div className={styles.step}>
                    <h2 className={styles.stepTitle}>
                       <span>Step 2:</span> Choose a {floorplanName} Floor Plan Elevation
                    </h2>
                    <div className={styles.optionsGrid}>
                        {elevations.map((elevation, index) => (
                            <div
                                key={index}
                                className={`${styles.optionCard} ${
                                    selectedElevation?.id === elevation.id ? styles.selected : ''
                                }`}
                                onClick={() => {
                                    setSelectedElevation(elevation);
                                    // Reset scene selection when elevation changes
                                    setSelectedScene(null);
                                }}
                            >
                                <img 
                                    src={elevation.image} 
                                    alt={elevation.name}
                                    className={styles.optionImage}
                                />
                                <p className={styles.optionLabel}>{elevation.name}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Step 3: Choose Scene */}
                <div className={styles.step}>
                    <h2 className={styles.stepTitle}>
                        <span>Step 3:</span> Choose a {floorplanName} Floor Plan Scene
                    </h2>
                    {!selectedElevation ? (
                        <p className={styles.instructionText}>Please select an elevation first</p>
                    ) : filteredScenes.length === 0 ? (
                        <p className={styles.instructionText}>No scenes available for this elevation</p>
                    ) : (
                        <div className={styles.optionsGrid}>
                            {filteredScenes.map((scene, index) => (
                                <div
                                    key={index}
                                    className={`${styles.optionCard} ${
                                        selectedScene?.area === scene.area && selectedScene?.elevationId === scene.elevationId ? styles.selected : ''
                                    }`}
                                    onClick={() => setSelectedScene(scene)}
                                >
                                    <img 
                                        src={scene.image} 
                                        alt={scene.name}
                                        className={styles.optionImage}
                                    />
                                    <p className={styles.optionLabel}>{scene.name}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Start Designing Button */}
                <button
                    className={`${styles.startButton} ${!isComplete ? styles.disabled : ''}`}
                    onClick={handleStartDesigning}
                    disabled={!isComplete}
                >
                    Start Designing
                </button>
            </div>
        </PopUpModal>
    );
}
